/**
 * BULK sibling of lifecycleBundleService.js's `fetchLifecyclePeriodBundle` —
 * extracted (2026-09-17) purely to keep lifecycleBundleService.js under the
 * 500-line cap; this file is re-exported from there so every existing
 * `import { fetchBulkLifecyclePeriodBundles } from '.../lifecycleBundleService'`
 * keeps working unchanged. See that file's docblock for the shared selection
 * rules this mirrors (MIRROR FOOTGUN: the DB trigger `_stage_lifecycle_invoice_internal`).
 */

import { supabase } from '../supabase';
import { fetchAllRowsInChunks } from '../utils/async/fetchAllRowsInChunks';
import { derivePeriodDuplicates } from '../utils/job/oneTimeServiceDuplicates';
import { tenantMidnightUtcIso } from '../utils/date/tenantDate';
import { getFieldReadTables } from '../utils/job/fieldReadTables';
import { getCurrentTenantRole, isOperationsSession } from '../utils/auth/currentTenantRoleStore';
import {
  resolveBulkWindowStarts,
  assembleBulkLifecycleBundles,
  buildSentPaidBilledJobIdsByCustomer,
} from '../utils/customer/bulkLifecycleBundles';
import { seedPoolPeriodInstantCache } from '../utils/schedule/poolPeriodInstantCount';
import { addOneDay, LIVE_INVOICE_STATUSES, canReadInvoicesTable } from './lifecycleBundleService';

/**
 * Batched sibling of `fetchLifecyclePeriodBundle` for the BULK review modal.
 * Computes, for EACH selected customer, the jobs that would stage into the
 * opening/closing invoice — using the SAME per-customer window logic as the
 * trigger, but in a handful of batched queries instead of N× round-trips.
 *
 * MIRROR FOOTGUN (same as fetchLifecyclePeriodBundle): this reconstructs
 * `stage_lifecycle_invoice`'s selection client-side. If that trigger's
 * eligibility changes, change BOTH this and fetchLifecyclePeriodBundle.
 *
 * Unlike `scanBulkLifecycleDuplicates` (which approximates with a season-year
 * scope for a quick "who to look at" warning), this resolves the EXACT
 * per-customer window so the $ totals/counts shown in the review modal match
 * what actually stages.
 *
 * @param {{
 *   tenantId: string,
 *   customerIds: string[],
 *   context: 'opening' | 'closing',
 *   transitionDate: string,  // 'YYYY-MM-DD' — tenant-today when previewing
 *   tz?: string,  // IANA timezone; defaults to UTC
 * }} params
 * @returns {Promise<Map<string, {
 *   jobCount: number,
 *   total: number,
 *   jobs: Array<{ id, date, total, unpriced_line_count, lines: Array }>,
 *   windowStart: string | null,
 *   windowUnknown: boolean,  // FIXED CONTRACT (RD-ROLE-005b B1, 2026-09-18) —
 *     true ONLY for an operations session, which has no readable data source
 *     for this bundle at all (see the guard below). Semantic: "this session
 *     cannot resolve the period window because it has no readable data
 *     source — render a pending/unavailable affordance, never a count."
 *     Always `false` on the admin/billing/field paths — the field is always
 *     present so a consumer never has to distinguish `false` from
 *     `undefined`. Do NOT rename this field or repurpose its meaning; a UI
 *     consumer keys on it directly.
 *   billedJobIds: string[],
 *   billedExclusionUnknown: boolean,  // true when the caller could not
 *     VERIFY nothing is billed — either it has no `invoices` SELECT (a field
 *     role) or its tenant role hasn't resolved yet. GROUNDWORK ONLY: no
 *     consumer reads this field today (2026-09-18) — see the docblock on
 *     `canReadInvoicesTable` (lifecycleBundleService.js) for why it must
 *     never be inferred from an empty `billedJobIds` alone.
 * }>>}  keyed by customerId. Every requested id is present; customers with no
 *       eligible jobs map to { jobCount: 0, total: 0, jobs: [] } (windowStart
 *       still carries the REAL resolved window when one exists — see
 *       emptyBundleMap).
 * @throws {Error} on any Supabase query failure
 */
export async function fetchBulkLifecyclePeriodBundles({ tenantId, customerIds, context, transitionDate, tz = 'UTC' }) {
  if (!Array.isArray(customerIds) || customerIds.length === 0) return new Map();

  // RD-ROLE-005b B1 (2026-09-18): operations lost base SELECT on jobs/
  // job_line_items (20260805140100) and customers (20260805160100), and was
  // never granted SELECT on customer_status_changes at all (it isn't in that
  // policy's role list). EVERY query this fetcher would run for an
  // operations session is guaranteed to return 0 rows under RLS — no error,
  // no signal. Short-circuit BEFORE issuing any query rather than let a
  // guaranteed-empty result masquerade as a confident "0 visitas" for a
  // customer that may have real visits. windowUnknown is a FIXED CONTRACT —
  // see the field's own docblock above.
  if (isOperationsSession()) {
    return emptyBundleMap(customerIds, new Map(), true, true);
  }

  const nextDay = addOneDay(transitionDate);
  // RD-ROLE-005b field-read-routing fix (2026-09-18): a blind field role
  // (worker, or a supervisor whose tenant removed price visibility) reads 0
  // rows — NO ERROR — from base `customers`/`jobs`/`job_line_items`. This was
  // the root cause of the Apertura/Cierre "1 visita, 0 rows" bug: the jobs
  // query below silently returned empty, which threw away an ALREADY-
  // RESOLVED windowStartByCustomer (customer_status_changes is NOT price-
  // gated, so that part always worked) and forced every customer's window
  // back to null — which then made extrasForPoolPeriod fall back to its
  // "today only" convenience and made the in-memory count uncapped. Route
  // every table read that RD-ROLE-005b price-gated through the same table
  // resolver its single-customer sibling (fetchLifecyclePeriodBundle) uses.
  const tables = getFieldReadTables();
  const isBaseJobsTable = tables.jobs === 'jobs';
  // A field role (worker/supervisor) never has base `invoices` SELECT,
  // visible or blind (canReadInvoicesTable mirrors the admin/billing-only
  // RLS policy) — skip that query rather than silently reading its 0 rows
  // as "nothing is billed."
  const canReadInvoices = canReadInvoicesTable(getCurrentTenantRole());
  // B3 (2026-09-18): canReadInvoicesTable fails OPEN on a null/unresolved
  // role (query invoices anyway) so a mid-resolution admin never loses its
  // read — but the CLAIM "we verified nothing is billed" must not fail open
  // the same way. A null role means the caller doesn't yet know if it's
  // even ALLOWED to read invoices, so it cannot claim it checked.
  const billedExclusionUnknown = !canReadInvoices || !getCurrentTenantRole();

  // ── 1. All qualifying status-change rows (desc) for these customers ─────────
  // customer_status_changes SELECT is NOT price-gated (admin/supervisor/
  // billing tenant-wide, worker via customer_assigned_to_caller) — unaffected
  // by field-role routing, queried as-is.
  const { data: changeRows, error: changeErr } = await fetchAllRowsInChunks(customerIds, (chunk, selectOpts) => supabase
    .from('customer_status_changes')
    .select('customer_id, changed_at', selectOpts)
    .eq('tenant_id', tenantId)
    .in('customer_id', chunk)
    .eq('to_status', context)
    // UTC instant of TENANT-LOCAL midnight on nextDay — see the single-
    // customer fetch (lifecycleBundleService.js) for why this can't be a
    // bare UTC bound.
    .lte('changed_at', tenantMidnightUtcIso(nextDay, tz))
    .order('changed_at', { ascending: false })
    .order('id'));
  if (changeErr) throw new Error(`fetchBulkLifecyclePeriodBundles: window_start query failed — ${changeErr.message}`);

  // Fallback created_at for customers with no change row.
  const haveChange = new Set((changeRows ?? []).map((r) => String(r.customer_id)));
  const missing = customerIds.filter((id) => !haveChange.has(String(id)));
  let customerCreatedRows = [];
  if (missing.length) {
    const { data: custRows, error: custErr } = await fetchAllRowsInChunks(missing, (chunk, selectOpts) => supabase
      .from(tables.customers)
      .select('id, created_at', selectOpts)
      .eq('tenant_id', tenantId)
      .in('id', chunk)
      .order('id'));
    if (custErr) throw new Error(`fetchBulkLifecyclePeriodBundles: customer fallback query failed — ${custErr.message}`);
    customerCreatedRows = custRows ?? [];
  }

  // Window resolution + earliest lower bound for the jobs query (pure helper).
  const windowStartByCustomer = resolveBulkWindowStarts({
    customerIds, changeRows: changeRows ?? [], customerCreatedRows, tz,
  });
  const windows = [...windowStartByCustomer.values()];
  if (windows.length === 0) return emptyBundleMap(customerIds, windowStartByCustomer, billedExclusionUnknown);
  const earliest = windows.reduce((a, b) => (a < b ? a : b));

  // ── 2. In-context jobs across the union window; assembler floors per-customer.
  // field_jobs carries no total/unpriced_line_count — selecting either there
  // is a hard error, not 0 rows (RD-ROLE-005b).
  const jobsSelect = isBaseJobsTable
    ? 'id, customer_id, date, total, unpriced_line_count'
    : 'id, customer_id, date';
  const { data: jobRows, error: jobsErr } = await fetchAllRowsInChunks(customerIds, (chunk, selectOpts) => supabase
    .from(tables.jobs)
    .select(jobsSelect, selectOpts)
    .eq('tenant_id', tenantId)
    .in('customer_id', chunk)
    .eq('job_context', context)
    .gte('date', earliest)
    .lte('date', transitionDate)
    .order('date', { ascending: true })
    .order('id'));
  if (jobsErr) throw new Error(`fetchBulkLifecyclePeriodBundles: jobs query failed — ${jobsErr.message}`);

  // ── 3. Billed exclusion — seeds instant-count cache. Widened (money-fences,
  // 2026-09-17) from ('sent','paid') to every LIVE status; see the
  // single-customer fetch above for why. Skipped entirely for a field role —
  // it has no `invoices` SELECT (always 0 rows, admin/billing-only RLS) —
  // rather than letting that 0-row read masquerade as "nothing is billed."
  let invoiceRows = [];
  if (canReadInvoices) {
    const { data, error: invErr } = await fetchAllRowsInChunks(customerIds, (chunk, selectOpts) => supabase
      .from('invoices')
      .select('id, customer_id, snapshot', selectOpts)
      .eq('tenant_id', tenantId)
      .in('customer_id', chunk)
      .in('status', LIVE_INVOICE_STATUSES)
      .is('deleted_at', null)
      .order('id'));
    if (invErr) throw new Error(`fetchBulkLifecyclePeriodBundles: invoices query failed — ${invErr.message}`);
    invoiceRows = data ?? [];
  }

  seedPoolPeriodInstantCache({
    tenantId,
    context,
    customerIds,
    windowStartByCustomer,
    billedByCustomer: buildSentPaidBilledJobIdsByCustomer(invoiceRows),
  });

  if (!jobRows || jobRows.length === 0) return emptyBundleMap(customerIds, windowStartByCustomer, billedExclusionUnknown);

  const jobIds = jobRows.map((j) => j.id);
  // field_job_line_items carries no unit_price (RD-ROLE-005b).
  const lineItemsSelect = isBaseJobsTable
    ? 'job_id, type, name, unit_price, quantity, catalog_id'
    : 'job_id, type, name, quantity, catalog_id';
  const { data: lineRows, error: lineErr } = await fetchAllRowsInChunks(jobIds, (chunk, selectOpts) => supabase
    .from(tables.jobLineItems)
    .select(lineItemsSelect, selectOpts)
    .eq('tenant_id', tenantId)
    .in('job_id', chunk)
    .order('id'));
  if (lineErr) throw new Error(`fetchBulkLifecyclePeriodBundles: line items query failed — ${lineErr.message}`);

  // ── 4. Pure reduction — the single source of truth for the window logic ─────
  return assembleBulkLifecycleBundles({
    customerIds,
    windowStartByCustomer,
    jobRows,
    invoiceRows,
    lineRows: lineRows ?? [],
    transitionDate,
    billedExclusionUnknown,
  });
}

/**
 * Map of every requested id → an empty bundle. `windowStartByCustomer` is
 * threaded through even on an empty result (2026-09-18 fix) — the old
 * version always stamped `windowStart: null`, which silently discarded a
 * REAL resolved window whenever the jobs query legitimately (or, pre-fix,
 * spuriously under RLS) came back empty. A caller (extrasForPoolPeriod) can
 * then tell "we resolved a real window and there's just nothing in it" apart
 * from "we never resolved a window at all."
 *
 * `windowUnknown` (B1, 2026-09-18) defaults false so every non-operations
 * caller of this helper (the "no windows resolved" / "no jobs" early-return
 * paths) gets an explicit `false` rather than an absent key — the operations
 * short-circuit above is the ONLY caller that passes `true`.
 */
function emptyBundleMap(customerIds, windowStartByCustomer = new Map(), billedExclusionUnknown = false, windowUnknown = false) {
  const m = new Map();
  for (const id of customerIds) {
    const cid = String(id);
    m.set(cid, {
      jobCount: 0,
      total: 0,
      jobs: [],
      windowStart: windowStartByCustomer.get(cid) ?? null,
      windowUnknown,
      billedJobIds: [],
      billedExclusionUnknown,
    });
  }
  return m;
}

/**
 * Pre-flight duplicate scan for a BULK lifecycle transition. For each selected
 * customer, checks whether their in-context jobs (this season) contain a
 * duplicate non-maintenance service, and returns ONLY the flagged customers
 * (with name + the duplicated service names) so the caller can warn before
 * bulk-staging N invoices.
 *
 * Deliberate approximation (NOT the exact trigger window): scoped by
 * `job_context` + `season_year` — close enough for a "which customers to look
 * at" warning; each flagged customer still gets the accurate per-customer
 * summary when reviewed individually. Three batched queries total (not N×).
 *
 * @param {{ tenantId, customerIds: string[], context: 'opening'|'closing', seasonYear: number, servicesById: object|Map }} params
 * @returns {Promise<Array<{ customerId: string, customerName: string, duplicateServiceNames: string[] }>>}
 */
export async function scanBulkLifecycleDuplicates({ tenantId, customerIds, context, seasonYear, servicesById }) {
  if (!Array.isArray(customerIds) || customerIds.length === 0) return [];

  // 1. In-context jobs for all selected customers this season.
  const { data: jobRows, error: jobsErr } = await fetchAllRowsInChunks(customerIds, (chunk, selectOpts) => supabase
    .from('jobs')
    .select('id, customer_id', selectOpts)
    .eq('tenant_id', tenantId)
    .in('customer_id', chunk)
    .eq('job_context', context)
    .eq('season_year', seasonYear)
    .order('id'));
  if (jobsErr) throw new Error(`scanBulkLifecycleDuplicates: jobs query failed — ${jobsErr.message}`);
  if (!jobRows || jobRows.length === 0) return [];

  // 2. Service line items for those jobs.
  const jobIds = jobRows.map((j) => j.id);
  const { data: lineRows, error: lineErr } = await fetchAllRowsInChunks(jobIds, (chunk, selectOpts) => supabase
    .from('job_line_items')
    .select('id, job_id, name, catalog_id', selectOpts)
    .eq('tenant_id', tenantId)
    .eq('type', 'service')
    .in('job_id', chunk)
    .order('id'));
  if (lineErr) throw new Error(`scanBulkLifecycleDuplicates: line items query failed — ${lineErr.message}`);

  // 3. Group jobs+lines per customer → run the shared duplicate derivation.
  const linesByJobId = new Map();
  for (const line of lineRows ?? []) {
    if (!line?.job_id) continue;
    const k = String(line.job_id);
    if (!linesByJobId.has(k)) linesByJobId.set(k, []);
    linesByJobId.get(k).push(line);
  }
  const jobsByCustomer = new Map();
  for (const j of jobRows) {
    const cid = String(j.customer_id);
    if (!jobsByCustomer.has(cid)) jobsByCustomer.set(cid, []);
    jobsByCustomer.get(cid).push({ id: String(j.id), lines: linesByJobId.get(String(j.id)) ?? [] });
  }

  const flagged = [];
  for (const [cid, jobs] of jobsByCustomer.entries()) {
    const dup = derivePeriodDuplicates(jobs, servicesById);
    if (dup.hasDuplicates) {
      flagged.push({ customerId: cid, duplicateServiceNames: dup.duplicateServiceNames });
    }
  }
  if (flagged.length === 0) return [];

  // 4. Resolve display names for the flagged customers only.
  const flaggedIds = flagged.map((f) => f.customerId);
  const { data: custRows } = await fetchAllRowsInChunks(flaggedIds, (chunk, selectOpts) => supabase
    .from('customers')
    .select('id, full_name', selectOpts)
    .eq('tenant_id', tenantId)
    .in('id', chunk)
    .order('id'));
  const nameById = new Map((custRows ?? []).map((c) => [String(c.id), c.full_name]));

  return flagged.map((f) => ({ ...f, customerName: nameById.get(f.customerId) || '' }));
}
